import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';

import { fetch, objectToQueryString } from 'later/utils/fetch';

import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type DialogManagerService from 'later/services/dialog-manager';
import type ErrorsService from 'later/services/errors';
import type { PopupWindowFeatures } from 'shared/lib/popup-instance';
import type PopupManagerService from 'shared/services/shared/popup-manager';

export const META_MARKETING_API_POPUP_NAME = 'meta_marketing_api_oauth';

interface MetaMarketingApiPopupResult {
  new_meta_ad_user_id?: string;
  imported_count?: number;
  failed_count?: number;
  error?:
    | 'already_connected'
    | 'auth_missing'
    | 'auth_failure'
    | 'ad_accounts_fetch_failed'
    | 'meta_account_mismatch'
    | string;
}

export default class MetaMarketingApiAuthService extends Service {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare dialogManager: DialogManagerService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service('shared/popup-manager') declare popupManager: PopupManagerService;
  @service declare router: RouterService;
  @service declare store: Store;

  @tracked showAssignmentsModal = false;
  // Set true when the connect task opens the modal post-OAuth so the close
  // handler can fire a success toast — and only then. Manual reopens of the
  // modal must NOT toast.
  @tracked pendingConnectSuccess = false;

  // Builds the OAuth URL, opens the popup, and resolves with its postMessage
  // payload (or undefined if the popup errors — in which case the failure alert
  // has already been shown and the popup closed). Shared by connect/refresh;
  // refresh passes `expected_uid` so the backend can fail closed on a
  // Meta-account switch.
  async #openOAuthPopup(extraParams: Record<string, string> = {}): Promise<MetaMarketingApiPopupResult | undefined> {
    const redirectPath = this.router.urlFor('user.profile-refresh-popup', META_MARKETING_API_POPUP_NAME).slice(1);
    // On ember's dev server, `/oauth/*` hits the SPA catch-all; point at the
    // backend origin directly so the OAuth route resolves.
    const isEmberDevServer = window.location.hostname === 'localhost' && window.location.port === '4200';
    const backendOrigin = isEmberDevServer ? 'https://localhost:3000' : '';
    const url = `${backendOrigin}/oauth/meta_marketing_api${objectToQueryString({
      redirect_path: redirectPath,
      ...extraParams
    })}`;

    const popup = this.popupManager.createPopup<MetaMarketingApiPopupResult>(META_MARKETING_API_POPUP_NAME, { url });

    const parentWidth = window.innerWidth;
    const parentHeight = window.innerHeight;
    const windowFeatures = {
      popup: true,
      width: 800,
      height: 600,
      left: (parentWidth - 800) / 2,
      top: (parentHeight - 600) / 2
    } satisfies PopupWindowFeatures;

    try {
      return await popup.open({ features: windowFeatures });
    } catch (error) {
      this.errors.log(error);
      this.showConnectFailure();
      return undefined;
    } finally {
      // popup.open() resolves on postMessage but does not close the popup itself.
      if (popup.isOpen) {
        popup.close();
      }
    }
  }

  connect = task(async () => {
    const data = await this.#openOAuthPopup();
    if (!data) return;

    if (data.error === 'already_connected') {
      this.alerts.alert(this.intl.t('account.meta_marketing_api.connect.already_connected.description'), {
        title: this.intl.t('account.meta_marketing_api.connect.already_connected.title'),
        sticky: true
      });
      return;
    }

    if (data.error === 'ad_accounts_fetch_failed') {
      this.alerts.alert(this.intl.t('account.meta_marketing_api.connect.ad_accounts_fetch_failed.description'), {
        title: this.intl.t('account.meta_marketing_api.connect.ad_accounts_fetch_failed.title'),
        sticky: true
      });
      return;
    }

    if (data.error) {
      this.showConnectFailure();
      return;
    }

    if (data.new_meta_ad_user_id) {
      // MetaAdUser is embedded on the user serializer; reload pulls it into the store.
      await this.auth.currentUserModel?.reload();
      // Refresh groups so embedded meta_ad_accounts reflect post-OAuth state.
      await this.store.findAll('group', { reload: true });
      // Open the assignments modal regardless of imported_count — covers the
      // reconnect-existing-account path where the backend returns 0 new
      // imports but the user still needs to manage their existing assignments.
      this.pendingConnectSuccess = true;
      this.showAssignmentsModal = true;
    }
  });

  refresh = task(async () => {
    // Refresh asserts the currently-connected Meta identity via expected_uid so
    // the backend can fail closed when the user signs in as a different Meta
    // account (instead of silently switching). Without a current MAU there is
    // nothing to refresh.
    const currentUid = this.auth.currentUserModel?.metaAdUser?.uid;
    if (!currentUid) {
      this.showConnectFailure();
      return;
    }

    const data = await this.#openOAuthPopup({ expected_uid: currentUid });
    if (!data) return;

    if (data.error === 'meta_account_mismatch') {
      const expectedName =
        this.auth.currentUserModel?.metaAdUser?.metaAccountName ??
        this.intl.t('account.meta_marketing_api.unknown_account_name');
      this.alerts.alert(
        this.intl.t('account.meta_marketing_api.refresh.meta_account_mismatch.description', { name: expectedName }),
        {
          title: this.intl.t('account.meta_marketing_api.refresh.meta_account_mismatch.title'),
          sticky: true
        }
      );
      return;
    }

    if (data.error === 'already_connected') {
      // Cross-user collision during refresh — reuses the connect.already_connected
      // strings (description includes the "disconnect there first" guidance).
      this.alerts.alert(this.intl.t('account.meta_marketing_api.connect.already_connected.description'), {
        title: this.intl.t('account.meta_marketing_api.connect.already_connected.title'),
        sticky: true
      });
      return;
    }

    if (data.error === 'ad_accounts_fetch_failed') {
      this.alerts.alert(this.intl.t('account.meta_marketing_api.connect.ad_accounts_fetch_failed.description'), {
        title: this.intl.t('account.meta_marketing_api.connect.ad_accounts_fetch_failed.title'),
        sticky: true
      });
      return;
    }

    if (data.error) {
      this.showConnectFailure();
      return;
    }

    if (data.new_meta_ad_user_id) {
      await this.auth.currentUserModel?.reload();
      await this.store.findAll('group', { reload: true });
      this.alerts.success(this.intl.t('account.meta_marketing_api.refresh.success.description'), {
        title: this.intl.t('account.meta_marketing_api.refresh.success.title')
      });
      // Open the assignments modal so the user can assign any ad accounts the
      // refresh picked up (BulkCreate is idempotent on existing rows and adds
      // newly-available ad accounts). pendingConnectSuccess stays false — that
      // flag is for the connect-flow success toast, not refresh.
      this.showAssignmentsModal = true;
    }
  });

  // Open the assignments modal outside the OAuth flow — e.g. the "Edit" button on
  // the Meta Business Account settings tab. pendingConnectSuccess stays false so
  // closeAssignmentsModal does not fire the connect success toast (that flag is the
  // connect task's mechanism, not the manual-edit path's).
  @action
  openAssignmentsModal(): void {
    this.showAssignmentsModal = true;
  }

  @action
  closeAssignmentsModal(): void {
    this.showAssignmentsModal = false;
    if (!this.pendingConnectSuccess) return;
    this.pendingConnectSuccess = false;
    const name = this.auth.currentUserModel?.metaAdUser?.metaAccountName;
    if (!name) return;
    this.alerts.success(this.intl.t('account.meta_marketing_api.connect.success.description', { name }), {
      title: this.intl.t('account.meta_marketing_api.connect.success.title')
    });
  }

  disconnect = task(async () => {
    const metaAdUser = this.auth.currentUserModel?.metaAdUser;
    if (!metaAdUser) return;

    const confirmed = await this.dialogManager.confirmation(
      this.intl.t('account.meta_marketing_api.disconnect.confirm.title'),
      {
        description: this.intl.t('account.meta_marketing_api.disconnect.confirm.description'),
        confirmButton: this.intl.t('account.meta_marketing_api.disconnect.confirm.confirm_button')
      }
    );
    if (!confirmed) return;

    try {
      await metaAdUser.destroyRecord();
      // Reload to clear the embedded relationship from the current-user payload.
      await this.auth.currentUserModel?.reload();
      // MetaAdUser destroy cascades to meta_ad_accounts + meta_ad_account_groups;
      // reload groups so the per-group ad-account lists clear immediately.
      await this.store.findAll('group', { reload: true });
      this.alerts.success(this.intl.t('account.meta_marketing_api.disconnect.success.description'), {
        title: this.intl.t('account.meta_marketing_api.disconnect.success.title')
      });
    } catch (error) {
      this.errors.log(error);
      this.alerts.alert(this.intl.t('account.meta_marketing_api.disconnect.failure.description'), {
        title: this.intl.t('account.meta_marketing_api.disconnect.failure.title'),
        sticky: true
      });
    }
  });

  async bulkSetGroups(assignments: Array<{ meta_ad_account_id: string; group_ids: string[] }>): Promise<{
    succeeded: Array<{ meta_ad_account_id: string; group_ids: string[] }>;
    failed: Array<{ meta_ad_account_id: string; errors: string[] }>;
  }> {
    const raw = (await fetch('/api/v2/meta_ad_accounts/bulk_set_groups', {
      method: 'PUT',
      body: { assignments }
    })) as {
      succeeded: Array<{ meta_ad_account_id: number | string; group_ids: Array<number | string> }>;
      failed: Array<{ meta_ad_account_id: number | string; errors: string[] }>;
    };

    // Backend returns numeric IDs; normalize to strings for set-based comparison with Ember Data IDs.
    return {
      succeeded: raw.succeeded.map((s) => ({
        meta_ad_account_id: String(s.meta_ad_account_id),
        group_ids: s.group_ids.map(String)
      })),
      failed: raw.failed.map((f) => ({
        meta_ad_account_id: String(f.meta_ad_account_id),
        errors: f.errors
      }))
    };
  }

  private showConnectFailure(): void {
    this.alerts.alert(this.intl.t('account.meta_marketing_api.connect.failure.description'), {
      title: this.intl.t('account.meta_marketing_api.connect.failure.title'),
      sticky: true
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'meta-marketing-api-auth': MetaMarketingApiAuthService;
  }
}
